/**
 * Marketplace buyer-passport display adapter (plan p219 milestone 1 —
 * "Build the rich Browse Buyers surface ... plus a display adapter that
 * stops internal enums leaking to the UI").
 *
 * The pro-facing browse/passport wire shapes (`MarketplacePassportCard`,
 * `backend/internal/services/marketplace/offers/pro_dto.go`) carry the raw
 * backend enums for `goal`, `timeline`, `activity_bucket`, and `tier` —
 * `first_time`, `0_3_months`, `ready`, `t3`, etc. (see
 * `backend/internal/model/models.go` `ReadinessTimelineEnum`,
 * `backend/internal/services/onboarding/enums.go`, and
 * `backend/internal/services/consumer/passport.go`'s `passportActivity*`
 * constants). None of these tokens is fit for the DOM. This module is the
 * SINGLE place that maps them to the copy in the "Browse Buyers" design
 * board (`Partner Marketplace.dc.html` §B) — every marketplace surface that
 * renders one of these fields must route through here, never render the raw
 * wire value directly.
 *
 * Every mapper degrades an unknown/empty value to a humanized fallback
 * (underscores → spaces, sentence-cased) rather than surfacing a raw
 * underscore token — a backend enum addition must never render literal
 * `"some_new_goal"` in the UI while this module catches up.
 *
 * M3 restyle (plan p219 milestone 3) extended this module with the pro
 * offers-ledger vocabulary: `ledgerStatusMeta`/`ledgerTabForStatus` map the
 * `marketplace_offers.status` enum to the ledger board's chip copy + tone
 * and its All/Active/Accepted/Closed tab grouping, and `offerTermsLabel`
 * renders the rebate% + cash pair ("0.9% + $500") the ledger board shows
 * per row — reusing the shared offer card's `formatPercent`/`formatDollars`
 * so the two surfaces never drift on number formatting.
 */

import {
  formatDollars,
  formatPercent,
  itemLabel,
  type OfferCardItemLanes,
} from "@sill/shared/components/marketplace/offer-card";

/** `some_raw_token` → `"Some raw token"` — the fallback for an unmapped enum. */
function humanizeFallback(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const spaced = trimmed.replaceAll("_", " ").replaceAll("-", " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const GOAL_LABELS: Record<string, string> = {
  first_time: "First home",
  move_up: "Move-up",
  investor: "Investment",
  investment: "Investment",
  downsizing: "Downsizing",
};

/** Buyer goal enum → the "Browse Buyers" card copy (design board §B). */
export function goalDisplayLabel(goal: string | undefined): string {
  if (!goal) return "";
  return GOAL_LABELS[goal] ?? humanizeFallback(goal);
}

const TIMELINE_LABELS: Record<string, string> = {
  "0_3_months": "0–3 months",
  "3_6_months": "3–6 months",
  "6_12_months": "6–12 months",
  "12_plus": "12+ months",
};

const TIMELINE_LABELS_SHORT: Record<string, string> = {
  "0_3_months": "0–3 mo",
  "3_6_months": "3–6 mo",
  "6_12_months": "6–12 mo",
  "12_plus": "12+ mo",
};

/** Timeline enum → full copy, e.g. for the passport-detail facts list. */
export function timelineDisplayLabel(timeline: string | undefined): string {
  if (!timeline) return "";
  return TIMELINE_LABELS[timeline] ?? humanizeFallback(timeline);
}

/** Timeline enum → dense copy for the browse-card facts block. */
export function timelineDisplayLabelShort(
  timeline: string | undefined,
): string {
  if (!timeline) return "";
  return TIMELINE_LABELS_SHORT[timeline] ?? humanizeFallback(timeline);
}

const ACTIVITY_LABELS: Record<string, string> = {
  ready: "Ready to act",
  ready_to_act: "Ready to act",
  active: "Active",
  exploring: "Exploring",
};

/** `activity_bucket` enum → the browse-card activity chip copy. */
export function activityDisplayLabel(activity: string | undefined): string {
  if (!activity) return "";
  return ACTIVITY_LABELS[activity] ?? humanizeFallback(activity);
}

export interface MarketplaceTierDisplay {
  /** Dense badge text, e.g. "T3". */
  badge: string;
  /** The full tier label per the design board (distinct from the generic
   * CRM verification-tier copy in `components/tier/tier.ts` — the
   * marketplace passport uses its own wording: "Self-reported" /
   * "Document-backed" / "Securely verified"). */
  label: string;
}

const MARKETPLACE_TIER_LABELS: Record<string, string> = {
  t1: "Self-reported",
  t2: "Document-backed",
  t3: "Securely verified",
};

/** Buyer tier enum (`t1`/`t2`/`t3`) → the marketplace-specific badge + label. */
export function marketplaceTierMeta(
  tier: string | undefined,
): MarketplaceTierDisplay {
  if (!tier) return { badge: "", label: "Unknown" };
  const label = MARKETPLACE_TIER_LABELS[tier];
  const badge = tier.toUpperCase();
  return { badge, label: label ?? "Unknown" };
}

const MARKETPLACE_TIER_CLASS: Record<string, string> = {
  t1: "tier-self",
  t2: "tier-doc",
  t3: "tier-bank",
};

/** Buyer tier enum (`t1`/`t2`/`t3`) → the pro CRM verification-badge tone
 * class (`tier-self`/`tier-doc`/`tier-bank`). Single source for both the
 * browse `BuyerCard` and the composer `PassportFactsCard`. */
export function marketplaceTierClass(
  tier: string | undefined,
): string | undefined {
  return tier ? MARKETPLACE_TIER_CLASS[tier] : undefined;
}

/** `n` dollars → the compact per-side bracket token, e.g. `950000` → `"950k"`,
 * `1150000` → `"1.2M"`. Rounds in the integer domain to avoid the
 * `(1.15).toFixed(1) === "1.1"` float-rounding trap. */
function fmtBracketPart(n: number): string {
  if (n >= 1_000_000) {
    const tenths = Math.round(n / 100_000);
    return `${(tenths / 10).toFixed(1)}M`;
  }
  return `${Math.round(n / 1000)}k`;
}

/** Price-bracket band → the "BRACKET" fact string, e.g. `"$950k–1.2M"`. Never
 * an exact figure — the band is the anonymity boundary (ADR-038). */
export function priceBracketDisplayLabel(
  bracket: { low: number; high: number } | undefined,
): string {
  if (!bracket) return "";
  return `$${fmtBracketPart(bracket.low)}–${fmtBracketPart(bracket.high)}`;
}

/** "buyer" for exactly one, else "buyers" — so a count never reads "1 buyers". */
export function buyerNoun(count: number): string {
  return count === 1 ? "buyer" : "buyers";
}

// ── Offers ledger (M3 restyle) ───────────────────────────────────────────────

/** The offer's rebate% + cash pair, e.g. `"0.9% + $500"` — the ledger
 * board's "terms" column. Falls back to `0%`/`$0` for an absent lane so a
 * terminal row with an empty frozen `items` snapshot still renders a
 * well-formed terms string instead of a blank cell. */
export function offerTermsLabel(items: OfferCardItemLanes): string {
  const rebatePct =
    items.rebate?.find((item) => item.value != null)?.value ?? 0;
  const cashDollars =
    items.cash?.find((item) => item.value != null)?.value ?? 0;
  return `${formatPercent(rebatePct)} + ${formatDollars(cashDollars)}`;
}

/** `marketplace_offers.status` (mirrored FE-side as `MarketplaceOfferStatusV2`)
 * — the ledger board's chip vocabulary (design board §D "Offers ledger"):
 * Active/Accepted share the pro-green tone, Superseded is informational
 * blue, Expired/Buyer-changed are amber, Withdrawn is neutral. */
export interface LedgerStatusMeta {
  label: string;
  chipClassName: string;
}

const LEDGER_STATUS_META: Record<string, LedgerStatusMeta> = {
  active: {
    label: "Active",
    chipClassName: "border-pro/25 bg-pro-soft text-pro",
  },
  accepted: {
    label: "Accepted",
    chipClassName: "border-pro/25 bg-pro-soft text-pro",
  },
  withdrawn: {
    label: "Withdrawn",
    chipClassName: "border-border bg-muted text-fg-muted",
  },
  superseded: {
    label: "Superseded",
    chipClassName: "border-info/25 bg-info/10 text-info",
  },
  expired: {
    label: "Expired",
    chipClassName: "border-warning/25 bg-warning/10 text-warning",
  },
  closed_buyer_changed: {
    label: "Buyer changed",
    chipClassName: "border-warning/25 bg-warning/10 text-warning",
  },
};

/** Status enum → chip copy + tone. Degrades an unmapped status to a
 * humanized fallback with the neutral tone, never a raw enum token. */
export function ledgerStatusMeta(status: string): LedgerStatusMeta {
  return (
    LEDGER_STATUS_META[status] ?? {
      label: humanizeFallback(status),
      chipClassName: "border-border bg-muted text-fg-muted",
    }
  );
}

/** Close-reason copy is explanatory only for terminal/closed rows. Active and
 * accepted already communicate their complete state in the status chip, and
 * backend transition reasons such as the literal "accepted" would otherwise
 * duplicate that chip as low-emphasis noise. */
export function shouldShowLedgerCloseReason(status: string): boolean {
  return ledgerTabForStatus(status) === "closed";
}

// ── Connections ledger — held-row decline/retry copy (plan 239, M6-B M9) ────

/**
 * `connectionLedgerDTO.last_charge_error` (a Stripe decline CODE, e.g.
 * `"card_declined"` / `"insufficient_funds"` — never a raw Stripe error
 * message, per ADR-030 D13) → plain language for the held-row expansion
 * (design §B4: "Card declined — insufficient funds"). An unmapped/unknown
 * code degrades to a generic "update your payment method" copy rather than
 * ever rendering the raw enum token.
 */
const DECLINE_REASON_LABELS: Record<string, string> = {
  card_declined: "Card declined",
  insufficient_funds: "Card declined — insufficient funds",
  expired_card: "Card declined — the card has expired",
  incorrect_cvc: "Card declined — incorrect security code",
  processing_error: "Card declined — a processing error occurred",
  lost_card: "Card declined",
  stolen_card: "Card declined",
  generic_decline: "Card declined",
  webhook_payment_failed: "Card declined",
};

/** Decline code → plain-language copy for the held-row expansion. */
export function declineReasonLabel(code: string | undefined): string {
  if (!code) return "";
  return (
    DECLINE_REASON_LABELS[code] ?? "Card declined — update your payment method"
  );
}

/**
 * Retry-status copy for a held row (design §B4: "We'll retry automatically ·
 * buyer can cancel after day 3"). The connection workflow's retry cadence
 * (24h/72h timers, day-3 buyer escape, day-7 abandon — tech-spec §D6) is
 * uniform across every held row, so the copy itself doesn't vary by
 * `retry_count`; a non-zero `retry_count` appends how many attempts have
 * already run so the pro isn't left guessing whether anything has happened
 * yet.
 */
export function retryStatusLabel(retryCount: number | undefined): string {
  const base = "We'll retry automatically · buyer can cancel after day 3";
  if (!retryCount) return base;
  const times = retryCount === 1 ? "1 time" : `${retryCount} times`;
  return `${base} (retried ${times} so far)`;
}

export type LedgerTabKey = "all" | "active" | "accepted" | "closed";

/** Groups a raw status into the ledger board's four filter tabs
 * (design board §D: "All · 9 / Active · 4 / Accepted · 1 / Closed · 4") —
 * every terminal status (withdrawn/superseded/expired/closed_buyer_changed)
 * folds into "Closed". */
export function ledgerTabForStatus(status: string): LedgerTabKey {
  if (status === "active") return "active";
  if (status === "accepted") return "accepted";
  return "closed";
}

/**
 * The Connections ledger's "Needs attention" membership: `charging` (still
 * settling payment) and `held` (payment stalled/retrying). The ONE predicate
 * behind that tab and the sidebar's Connections badge, so the badge can never
 * count a different set than the tab it links to.
 */
export function connectionNeedsAttention(status: string): boolean {
  return status === "charging" || status === "held";
}

// ── Frozen item-lane summary (Requests card §B3, Ledger table s8) ──────────

/**
 * A frozen standing-offer's item lanes (rebate/cash/perks) → the single
 * itemized one-line summary string, e.g. `"0.9% rebate + $500 home
 * warranty"` — the SINGLE formatter for this, shared by the Requests card's
 * dashed "OFFER THEY ACCEPTED · FROZEN" box (s6) and the connections ledger
 * table (s8), so the two surfaces never drift on lane-join punctuation or
 * number formatting. Reuses the shared offer card's `formatPercent`/
 * `formatDollars`/`itemLabel` — the same conventions as `offerTermsLabel`
 * above, joined into ONE string instead of a fixed rebate/cash pair (a
 * frozen snapshot can carry any mix of lanes, including perks-only).
 * Degrades an absent/empty lane set to `""` rather than a placeholder — the
 * caller renders its own "no items" fallback.
 */
export function formatItemLanesSummary(
  lanes: OfferCardItemLanes | undefined,
): string {
  if (!lanes) return "";
  const parts: string[] = [];
  for (const item of lanes.rebate ?? []) {
    if (item.value == null) continue;
    parts.push(`${formatPercent(item.value)} rebate`);
  }
  for (const item of lanes.cash ?? []) {
    if (item.value == null) continue;
    parts.push(
      `${formatDollars(item.value)} ${itemLabel(item.type).toLowerCase()}`,
    );
  }
  for (const item of lanes.perks ?? []) {
    parts.push(itemLabel(item.type).toLowerCase());
  }
  return parts.join(" + ");
}

// ── Requests inbox shared vocabulary (requests card + request detail page) ──
// Shared by `MarketplaceRequestsPage` and `MarketplaceRequestDetailPage`
// (M9.7) so the decline-reason options and expiry/requested-ago copy never
// drift between the list row and the full-page detail view.

/** Decline-reason radio-card options for the requests inbox's decline flow. */
export const REQUEST_DECLINE_REASONS: Array<{ value: string; label: string }> =
  [
    { value: "not_a_fit", label: "Not a fit for my services" },
    { value: "no_capacity", label: "No capacity right now" },
    { value: "outside_area", label: "Outside my service area" },
    { value: "other", label: "Other" },
  ];

/** `expires_at` ISO string → the requests inbox's expiry chip copy
 * ("Expires 4h" / "Expires in 3 days" / "Expired"). */
export function requestExpiryChipLabel(expiresAt: string): string {
  const target = new Date(expiresAt);
  const diffMs = target.getTime() - Date.now();
  const hours = Math.ceil(diffMs / (1000 * 60 * 60));
  if (hours <= 0) return "Expired";
  if (hours < 72) return `Expires ${hours}h`;
  const days = Math.ceil(hours / 24);
  return days === 1 ? "Expires in 1 day" : `Expires in ${days} days`;
}

/** `expires_at` ISO string → whether the request's 72h buyer-consent window
 * has already lapsed against the CLIENT clock. Drives the expiry chip's
 * muted/danger tone (vs the live amber countdown) and disables Connect +
 * Decline on a card the pro shouldn't be able to act on anymore — "View
 * offer" stays enabled either way, since reading a stale offer is harmless.
 * This is a client-clock UX affordance only; the server-side guard
 * (`connections.ErrRequestExpired`) is the actual enforcement boundary. */
export function isRequestExpired(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() <= Date.now();
}

/** Expiry chip Tailwind classes: amber while live, muted/danger once past
 * `expires_at` — the "odd/negative countdown chip" the seeding flagged is
 * fixed by this tone switch (the label itself already said "Expired"; only
 * the color was still the live-countdown amber). */
export function requestExpiryChipClassName(expiresAt: string): string {
  return isRequestExpired(expiresAt)
    ? "border-destructive/25 bg-destructive/10 text-destructive"
    : "border-warning/25 bg-warning/10 text-warning";
}

/** `created_at` ISO string → "Requested N (hour|day)(s) ago" — computed from
 * the real timestamp, never a fabricated goal string. */
export function requestRelativeAgoLabel(createdAt: string): string {
  const created = new Date(createdAt).getTime();
  const diffMs = Math.max(0, Date.now() - created);
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return "Requested just now";
  if (hours < 24) return `Requested ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `Requested ${days} day${days === 1 ? "" : "s"} ago`;
}

/** `"Buyer #02D2"` → `"02"` — the numbered avatar circle's short token
 * (design §B3 mock: the first two characters after the `#`). */
export function requestAvatarToken(displayCode: string): string {
  const afterHash = displayCode.split("#").pop() ?? displayCode;
  const token = afterHash.trim() || displayCode;
  return token.slice(0, 2).toUpperCase();
}

// ── Connections ledger — cause pill + fee cell (M6-B M9.9 operator polish) ──

/**
 * `connections.Connection.Cause` (the wire `cause` field — mirrors
 * `connectionLedgerDTO.Cause` exactly) is the connection's ORIGIN,
 * `"active_offer"` or `"standing_offer"` (`backend/internal/services/
 * marketplace/connections/lifecycle.go` originActiveOffer/
 * originStandingOffer) — NOT the internal pricing-cause vocabulary
 * (`"marketplace"`/`"retention"`) a prior version of this page's local
 * `causeLabel` mistakenly matched against, which meant every real
 * production row fell through to the raw-enum fallback. `retention` is
 * checked FIRST (a retention connection's own client, independent of its
 * origin) and wins regardless of `cause`; an unmapped/future cause degrades
 * to a humanized fallback rather than a raw snake_case token.
 */
const CONNECTION_CAUSE_LABELS: Record<string, string> = {
  active_offer: "Tailored offer",
  standing_offer: "Standing offer",
};

/** cause + retention → the ledger's CAUSE pill copy (design §B4). */
export function connectionCauseLabel(
  cause: string,
  retention: boolean,
): string {
  if (retention) return "Your client";
  return CONNECTION_CAUSE_LABELS[cause] ?? humanizeFallback(cause);
}

/** The backend's fee line is the complete settlement projection for the row. */
export function connectionFeeCellLabel(row: {
  fee_line: string;
  amount_charged_cents?: number;
}): string {
  return row.fee_line;
}
